import { Component, OnDestroy, ViewChild, HostBinding,
         ElementRef,Inject, Optional, ViewEncapsulation} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Directionality } from '@angular/cdk/bidi';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { environment } from 'environments/environment';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';

// import { SettingsService, AppSettings } from '@core';
import { AppDirectionality } from '@shared';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SharedService } from '../../services/core/shared.service';
import { SettingsService } from 'app/services/core/settings.service';
import { AppSettings } from '@shared/core';

const MOBILE_MEDIAQUERY = 'screen and (max-width: 599px)';
const TABLET_MEDIAQUERY = 'screen and (min-width: 600px) and (max-width: 959px)';
const MONITOR_MEDIAQUERY = 'screen and (min-width: 960px)';

@Component({
  selector: 'app-default-layout',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DefaultLayoutComponent implements OnDestroy {
  @ViewChild('sidenav', { static: true }) sidenav: MatSidenav;
  @ViewChild('content', { static: true }) content: MatSidenavContent;

  options = this.settings.getOptions();

  timedOut = false;
  lastPing?: Date = null;
  idleInterval: number = 9000; 
  timeoutInterval: number = 300; //logout 60 seconds after warning

  private environment = environment;
  private layoutChangesSubscription: Subscription;

  get isMobile(): boolean {
    return this.isMobileScreen;
  }

  private isMobileScreen = false;

  @HostBinding('class.matero-content-width-fix') get contentWidthFix() {
    return (
      this.isContentWidthFixed &&
      this.options.navPos === 'side' &&
      this.options.sidenavOpened &&
      !this.isMobile
    );
  }

  private isContentWidthFixed = true;

  @HostBinding('class.matero-sidenav-collapsed-fix') get collapsedWidthFix() {
    return (
      this.isCollapsedWidthFixed &&
      (this.options.navPos === 'top' || (this.options.sidenavOpened && this.isMobile))
    );
  }

  private isCollapsedWidthFixed = false;

  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private element: ElementRef,
    private settings: SettingsService,
    private idle: Idle,
    private sharedService: SharedService,
    private keepalive: Keepalive,
    public dialog: MatDialog,
    @Optional() @Inject(DOCUMENT) private document: Document,
    @Inject(Directionality) public dir: AppDirectionality)
  {
    this.dir.value = this.options.dir;
    this.document.body.dir = this.dir.value;

    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_MEDIAQUERY, TABLET_MEDIAQUERY, MONITOR_MEDIAQUERY])
      .subscribe(state => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[MOBILE_MEDIAQUERY];
        this.options.sidenavCollapsed = state.breakpoints[TABLET_MEDIAQUERY];
        this.isContentWidthFixed = state.breakpoints[MONITOR_MEDIAQUERY];
      });

    // TODO: Scroll top to container
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(e => {
      this.content.scrollTo({ top: 0 });
    });

    // Initialize project theme with options
    this.receiveOptions(this.options);

    //Timeout controls

    idle.setIdle(this.idleInterval);
    // sets a timeout period  seconds. after X seconds of inactivity, the user will be considered timed out.
    idle.setTimeout(this.timeoutInterval);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => {
      this.reset();
    });

    idle.onTimeout.subscribe(() => {
      this.dialog.closeAll();
      this.timedOut = true;
      // this.sharedService.logout();
      this.router.navigate(['/login']);
    });

    idle.onIdleStart.subscribe(() => {
      this.dialog.open(TimeoutDialog,
        {
          minWidth: '350px',
          panelClass: this.settings.theme === 'dark' ? 'theme-dark' : ''
        });
    });

    // sets the ping interval to 15 seconds
    this.keepalive.interval(15);

    this.keepalive.onPing.subscribe(() => {
      this.lastPing = new Date();
    });

    this.reset();
  }

  ngOnInit() {
    if (!this.isMobileScreen && this.environment.collapsibleSidebar) {
      this.toggleCollapsed();
    }
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  // TODO: Trigger when transition end
  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  sidenavCloseStart() {
    this.isContentWidthFixed = false;
  }

  sidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isMobile;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  receiveOptions(options: AppSettings): void {
    this.options = options;
    this.toggleDarkTheme(options);
    this.toggleDirection(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.element.nativeElement.classList.add('theme-dark');
    } else {
      this.element.nativeElement.classList.remove('theme-dark');
    }
  }

  toggleDirection(options: AppSettings) {
    this.dir.value = options.dir;
    this.document.body.dir = this.dir.value;
  }

  reset() {
    this.idle.watch();
    this.timedOut = false;
    this.dialog.closeAll();
  }
}

@Component({
  selector: 'timeout-dialog',
  templateUrl: './dialog-timeout.html'
})

export class TimeoutDialog {

  constructor() { }
}
